import { useClientPortalAPI, useQueryParams, useSubmit } from "@certn/api";
import {
  DropdownMenu,
  GhostIconButton,
  MenuItem,
  showToast,
} from "@certn/cascadia-react";
import { Trans, useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { useClientAccount } from "../../../hooks/useClientAccount";

interface BillingProfilesActionsDropdownProps {
  profileId: string;
  archived: boolean;
}

export function BillingProfilesActionDropdownMenu(
  props: BillingProfilesActionsDropdownProps
) {
  const { profileId, archived } = props;
  const { t } = useTranslation();
  const { client } = useClientAccount();
  const navigate = useNavigate();
  const location = useLocation();
  const isDetailView = location.pathname.includes(profileId);

  const { flatParams } = useQueryParams();

  const { mutate } = useClientPortalAPI(
    `/api/internal/client-portal/customer-accounts/${client?.id}/billing-profiles/`,
    {
      page: flatParams?.page,
      page_size: flatParams?.page_size,
      ordering: flatParams?.ordering,
      currency: flatParams?.currency,
      archived: flatParams?.archived || "ACTIVE",
    }
  );

  const { submit: archiveProfile } = useSubmit(
    `/api/internal/client-portal/billing-profiles/${profileId}/archive/`,
    {
      showToastOnError: true,
      onSuccess: async () => {
        await mutate();
        showToast(t("Billing profile archived"), { type: "success" });
      },
    }
  );
  const { submit: restoreProfile } = useSubmit(
    `/api/internal/client-portal/billing-profiles/${profileId}/restore/`,
    {
      onSuccess: async () => {
        await mutate();
        showToast(t("Billing profile restored"), { type: "success" });
      },
    }
  );
  const handleChangeStatus = () => {
    if (archived) {
      restoreProfile();
    } else {
      archiveProfile();
    }
  };

  return (
    <DropdownMenu
      triggerButton={
        <GhostIconButton
          testID="billing-profile-actions-expand-menu-button"
          size="small"
          icon="more_horiz"
          aria-label={t("Expand menu")}
        />
      }
      onItemAction={(key) => {
        switch (key) {
          case "view_invoices":
            navigate(
              profileId
                ? `/billing/invoices?profile_ids=${profileId}`
                : `/billing/invoices`
            );
            break;
          case "edit_profile":
            navigate(`${profileId}`);
            break;
          case "chang_profile_status":
            handleChangeStatus();
            break;
        }
      }}
    >
      {!isDetailView && (
        <>
          <MenuItem
            id="view_invoices"
            testID="billing-profile-table-view-invoices-button"
          >
            <Trans>View invoices</Trans>
          </MenuItem>

          <MenuItem
            id="edit_profile"
            testID="billing-profile-table-edit-profile-button"
          >
            <Trans>Edit profile</Trans>
          </MenuItem>
        </>
      )}

      <MenuItem
        id="chang_profile_status"
        testID="billing-profile-table-change-profile-status-button"
        textColor={archived ? undefined : "text-danger"}
      >
        {archived ? (
          <Trans>Restore profile</Trans>
        ) : (
          <Trans>Archive profile</Trans>
        )}
      </MenuItem>
    </DropdownMenu>
  );
}
